<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import {
  FAIR_PAGE_LOADER_BREATHE_DELAY_MS,
  FAIR_PAGE_LOADER_BREATHE_MS,
  FAIR_PAGE_LOADER_DRAW_MS,
  FAIR_PAGE_LOADER_FILL_MS,
  FAIR_PAGE_LOADER_LINE_PX,
  FAIR_PAGE_LOADER_MARK_PATH,
  FAIR_PAGE_LOADER_MARK_SIZE,
  FAIR_PAGE_LOADER_MARK_VIEWBOX,
  FAIR_PAGE_LOADER_MONOGRAM_PATH,
  FAIR_PAGE_LOADER_REVEAL_DELAY_MS,
  FAIR_PAGE_LOADER_REVEAL_MS,
  FAIR_PAGE_LOADER_SNAKE_RATIO,
  FAIR_PAGE_LOADER_SPIN_INTRO_MS,
  type FairPageLoaderMotion,
} from '@/ui/tokens/fair-page-loader.data'

interface FairPageLoaderMarkProps {
  /** Restart the draw / snake intro (e.g. each time loader opens). */
  replayKey?: number | string
  /** Mark width (draw) / box size (spin). */
  size?: number
  /** `draw` = route overlay: draw → fill → breathe · `spin` = short snake crawls FR contour */
  motion?: FairPageLoaderMotion
}

const props = withDefaults(defineProps<FairPageLoaderMarkProps>(), {
  replayKey: 0,
  size: FAIR_PAGE_LOADER_MARK_SIZE,
  motion: 'draw',
})

const ready = ref(false)

const viewBox = FAIR_PAGE_LOADER_MARK_VIEWBOX
const drawViewBox = `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`

const markStyle = computed(() => {
  if (props.motion === 'draw') {
    return {
      width: `${props.size}px`,
      height: `${(props.size * viewBox.height) / viewBox.width}px`,
      // Loader A — the outline is 1.2 screen px at any size (8.77 user units at 72 px).
      '--fair-page-loader-line': String((FAIR_PAGE_LOADER_LINE_PX * viewBox.width) / props.size),
      '--fair-page-loader-draw-ms': `${FAIR_PAGE_LOADER_DRAW_MS}ms`,
      '--fair-page-loader-reveal-delay-ms': `${FAIR_PAGE_LOADER_REVEAL_DELAY_MS}ms`,
      '--fair-page-loader-reveal-ms': `${FAIR_PAGE_LOADER_REVEAL_MS}ms`,
      '--fair-page-loader-breathe-delay-ms': `${FAIR_PAGE_LOADER_BREATHE_DELAY_MS}ms`,
      '--fair-page-loader-breathe-ms': `${FAIR_PAGE_LOADER_BREATHE_MS}ms`,
    }
  }
  return {
    width: `${props.size}px`,
    height: `${props.size}px`,
    '--fair-page-loader-spin-intro-ms': `${FAIR_PAGE_LOADER_SPIN_INTRO_MS}ms`,
    '--fair-page-loader-fill-ms': `${FAIR_PAGE_LOADER_FILL_MS}ms`,
  }
})

const markClass = computed(() => [
  `fair-page-loader__mark--${props.motion}`,
  { 'fair-page-loader__mark--ready': ready.value },
])

/** Normalized pathLength=1 → fixed dash math, no getTotalLength needed for crawl. */
const snakeDashStyle = computed(() => {
  const snake = FAIR_PAGE_LOADER_SNAKE_RATIO
  const gap = Math.max(1 - snake, 0.01)
  return {
    strokeDasharray: `${snake} ${gap}`,
    strokeDashoffset: 0,
  }
})

/** Re-arm the CSS animations: drop the ready class for a frame, then put it back. */
async function replay() {
  ready.value = false
  await nextTick()
  if (typeof requestAnimationFrame === 'undefined') {
    ready.value = true
    return
  }
  requestAnimationFrame(() => {
    ready.value = true
  })
}

onMounted(() => {
  void replay()
})

watch(
  () => [props.replayKey, props.motion] as const,
  () => {
    void replay()
  },
)
</script>

<template>
  <div
    class="fair-page-loader__mark"
    :class="markClass"
    :style="markStyle"
  >
    <svg
      v-if="motion === 'draw'"
      class="fair-page-loader__svg"
      :viewBox="drawViewBox"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        class="fair-page-loader__fill"
        pathLength="1"
        :d="FAIR_PAGE_LOADER_MARK_PATH"
      />
      <path
        class="fair-page-loader__line"
        pathLength="1"
        :d="FAIR_PAGE_LOADER_MARK_PATH"
      />
    </svg>

    <div
      v-else
      class="fair-page-loader__orbit"
    >
      <svg
        class="fair-page-loader__svg"
        viewBox="0 0 57.2887 57.2887"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          class="fair-page-loader__path fair-page-loader__path--ghost"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
        />
        <path
          class="fair-page-loader__path fair-page-loader__path--track"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
        />
        <path
          class="fair-page-loader__path fair-page-loader__path--stroke"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
          pathLength="1"
          :style="snakeDashStyle"
        />
      </svg>
    </div>
  </div>
</template>
